import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "@/lib/api";
import { Plane, Filter, X, ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";

const manufacturers = ["", "Cessna", "Piper", "Diamond", "Tecnam", "Beechcraft", "Bombardier", "Gulfstream", "Embraer", "Dassault"];
const types = ["", "Business Jet", "Turboprop", "Piston", "Trainer", "Helicopter"];
const regions = ["", "North America", "Europe", "Middle East", "Asia Pacific", "Africa", "Latin America"];
const statuses = ["", "Available", "Under Offer", "Sold"];

function priceLabel(a) {
  if (a.price_on_request || !a.price_usd) return "Price on request";
  return "USD " + Number(a.price_usd).toLocaleString();
}

function galleryOf(a) {
  const imgs = (a.images && a.images.length ? a.images : []).filter(Boolean);
  if (imgs.length) return imgs;
  return a.image_url ? [a.image_url] : [];
}

function Lightbox({ aircraft, onClose }) {
  const imgs = galleryOf(aircraft);
  const [idx, setIdx] = useState(0);
  const next = () => setIdx((i) => (i + 1) % imgs.length);
  const prev = () => setIdx((i) => (i - 1 + imgs.length) % imgs.length);

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[200] bg-[#FAFAF9]/95 backdrop-blur-xl overflow-y-auto"
      data-testid="listing-detail-modal"
    >
      <div className="min-h-full max-w-[1200px] mx-auto px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#9AA3AE]">
            {aircraft.manufacturer} · {aircraft.registration || aircraft.status}
          </div>
          <button onClick={onClose} data-testid="listing-detail-close" className="w-10 h-10 border border-[#0E1B2E]/15 hover:border-[#C8A97E] text-[#0E1B2E] flex items-center justify-center transition-colors">
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-7">
            <div className="relative aspect-[4/3] overflow-hidden border border-[#0E1B2E]/10 bg-[#F2F2EF]">
              {imgs.length > 0 && (
                <img src={imgs[idx]} alt={aircraft.model} className="w-full h-full object-cover" />
              )}
              {imgs.length > 1 && (
                <>
                  <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 border border-[#0E1B2E]/10 hover:border-[#C8A97E] text-[#0E1B2E] flex items-center justify-center transition-colors"><ChevronLeft size={18} /></button>
                  <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 border border-[#0E1B2E]/10 hover:border-[#C8A97E] text-[#0E1B2E] flex items-center justify-center transition-colors"><ChevronRight size={18} /></button>
                  <div className="absolute bottom-3 left-1/2 -translate-x-1/2 font-mono text-[10px] tracking-[0.28em] text-[#445060] bg-white/85 px-3 py-1">{idx + 1} / {imgs.length}</div>
                </>
              )}
            </div>
            {imgs.length > 1 && (
              <div className="mt-3 grid grid-cols-5 gap-2">
                {imgs.map((im, i) => (
                  <button key={i} onClick={() => setIdx(i)} className={`aspect-[4/3] overflow-hidden border ${i === idx ? "border-[#A5854C]" : "border-[#0E1B2E]/10"}`}>
                    <img src={im} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="col-span-12 lg:col-span-5">
            <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#A5854C]">{aircraft.status}</span>
            <h2 className="mt-3 font-serif text-[36px] md:text-[44px] text-[#0E1B2E] tracking-tight leading-[1]">{aircraft.model}</h2>
            <div className="mt-3 font-serif text-[22px] text-[#A5854C]">{priceLabel(aircraft)}</div>
            {aircraft.description && <p className="mt-6 text-[#6B7684] text-[15px] leading-relaxed">{aircraft.description}</p>}

            <div className="mt-8 grid grid-cols-2 gap-px bg-[#0E1B2E]/10 border border-[#0E1B2E]/10">
              {[
                ["Registration", aircraft.registration],
                ["Year", aircraft.year],
                ["Airframe Hours", aircraft.flight_hours],
                ["Engine", aircraft.engine_type],
                ["Serial No.", aircraft.serial_number],
                ["Region", aircraft.region],
                ["Type", aircraft.aircraft_type],
                ["Manufacturer", aircraft.manufacturer],
              ].filter(([, v]) => v !== undefined && v !== null && v !== "").map(([k, v]) => (
                <div key={k} className="bg-white p-4">
                  <div className="font-mono text-[9px] uppercase tracking-[0.24em] text-[#9AA3AE]">{k}</div>
                  <div className="mt-1 text-[#0E1B2E] text-[14px]">{v}</div>
                </div>
              ))}
            </div>

            <a href="/contact" data-testid="listing-detail-enquire" className="btn-gold mt-8 w-full py-4 text-[11px] uppercase tracking-[0.25em] font-mono inline-flex items-center justify-center gap-2">
              Enquire about this aircraft <ArrowUpRight size={14} />
            </a>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Listings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [f, setF] = useState({ manufacturer: "", aircraft_type: "", region: "", status: "" });

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const params = {};
      Object.entries(f).forEach(([k, v]) => v && (params[k] = v));
      try {
        const { data } = await api.get("/listings", { params });
        setItems(data);
      } finally { setLoading(false); }
    })();
  }, [f]);

  return (
    <main data-testid="page-listings" className="pt-32 min-h-screen bg-[#FAFAF9]">
      <section className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 md:py-24">
        <div className="flex items-center gap-3 text-[#A5854C] mb-8">
          <div className="hairline w-12"></div>
          <span className="font-mono text-[11px] uppercase tracking-[0.35em]">Aircraft Inventory</span>
        </div>
        <motion.h1
          initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}
          className="font-serif font-light tracking-[-0.02em] text-[#0E1B2E] text-[44px] md:text-[72px] leading-[1] max-w-4xl"
        >
          Selected aircraft <span className="italic text-[#A5854C]">under mandate.</span>
        </motion.h1>
        <p className="mt-8 max-w-2xl text-[#6B7684] text-[15px] leading-relaxed">
          Inventory is curated. Exclusive mandates are not publicly listed — inquire for the confidential book.
        </p>
      </section>

      <section className="border-t border-[#0E1B2E]/10">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-12 gap-8 py-12">
          <aside className="col-span-12 md:col-span-3 md:sticky md:top-24 self-start">
            <div className="flex items-center gap-2 mb-6 text-[#A5854C]">
              <Filter size={14} />
              <span className="font-mono text-[10px] uppercase tracking-[0.28em]">Filters</span>
            </div>
            {[
              ["manufacturer", "Manufacturer", manufacturers],
              ["aircraft_type", "Type", types],
              ["region", "Region", regions],
              ["status", "Status", statuses],
            ].map(([k, label, opts]) => (
              <div key={k} className="mb-6">
                <label className="block font-mono text-[10px] uppercase tracking-[0.28em] text-[#9AA3AE] mb-2">{label}</label>
                <select
                  data-testid={`filter-${k}`}
                  value={f[k]}
                  onChange={(e) => setF((s) => ({ ...s, [k]: e.target.value }))}
                  className="w-full bg-white border border-[#0E1B2E]/15 focus:border-[#A5854C] text-[#0E1B2E] px-3 py-2.5 text-[13px] outline-none transition-colors"
                >
                  {opts.map((o) => <option key={o} value={o}>{o || "Any"}</option>)}
                </select>
              </div>
            ))}
            <button
              onClick={() => setF({ manufacturer: "", aircraft_type: "", region: "", status: "" })}
              data-testid="filter-clear"
              className="btn-ghost text-[10px] uppercase tracking-[0.25em] font-mono px-4 py-2.5 w-full"
            >
              Clear filters
            </button>
          </aside>

          <div className="col-span-12 md:col-span-9">
            {loading ? (
              <div className="text-[#9AA3AE] font-mono text-[11px] uppercase tracking-[0.28em]" data-testid="listings-loading">Loading inventory…</div>
            ) : items.length === 0 ? (
              <div data-testid="listings-empty" className="border border-dashed border-[#0E1B2E]/15 py-24 md:py-32 flex flex-col items-center justify-center text-center">
                <div className="w-16 h-16 border border-[#C8A97E]/50 rotate-45 flex items-center justify-center mb-8">
                  <Plane className="text-[#A5854C] -rotate-45" size={26} />
                </div>
                <h3 className="font-serif text-[32px] md:text-[44px] text-[#0E1B2E] tracking-tight">Inventory coming soon.</h3>
                <p className="mt-4 max-w-md text-[#6B7684] text-[15px] leading-relaxed">
                  New mandates are added monthly. For current off-market opportunities, request the confidential aircraft book.
                </p>
                <a href="/contact" className="btn-gold mt-8 px-6 py-3 text-[11px] uppercase tracking-[0.25em] font-mono">Request Confidential Book</a>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {items.map((a) => {
                  const imgs = galleryOf(a);
                  return (
                    <button
                      key={a.id}
                      onClick={() => setActive(a)}
                      data-testid={`listing-card-${a.id}`}
                      className="text-left border border-[#0E1B2E]/10 bg-white hover:border-[#A5854C] transition-colors group"
                    >
                      {imgs.length > 0 && (
                        <div className="aspect-[16/10] overflow-hidden relative">
                          <img src={imgs[0]} alt={a.model} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" />
                          {imgs.length > 1 && (
                            <span className="absolute bottom-3 right-3 font-mono text-[10px] tracking-[0.2em] text-[#0E1B2E] bg-white/85 px-2 py-1">
                              {imgs.length} PHOTOS
                            </span>
                          )}
                        </div>
                      )}
                      <div className="p-6">
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#9AA3AE]">{a.manufacturer}{a.registration ? ` · ${a.registration}` : ""}</span>
                          <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#A5854C]">{a.status}</span>
                        </div>
                        <h3 className="mt-4 font-serif text-[24px] text-[#0E1B2E] tracking-tight">{a.model}</h3>
                        <div className="mt-2 font-serif text-[17px] text-[#A5854C]">{priceLabel(a)}</div>
                        <div className="mt-4 grid grid-cols-3 gap-3 text-[12px]">
                          <div><div className="text-[#9AA3AE] font-mono text-[10px] uppercase tracking-[0.22em]">Year</div><div className="text-[#0E1B2E]">{a.year}</div></div>
                          <div><div className="text-[#9AA3AE] font-mono text-[10px] uppercase tracking-[0.22em]">Hours</div><div className="text-[#0E1B2E]">{a.flight_hours ?? "—"}</div></div>
                          <div><div className="text-[#9AA3AE] font-mono text-[10px] uppercase tracking-[0.22em]">Region</div><div className="text-[#0E1B2E]">{a.region}</div></div>
                        </div>
                        <div className="mt-5 flex items-center gap-2 text-[#A5854C] text-[11px] uppercase tracking-[0.25em] font-mono opacity-70 group-hover:opacity-100 transition-opacity">
                          View details <ArrowUpRight size={13} />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>

      <AnimatePresence>
        {active && <Lightbox aircraft={active} onClose={() => setActive(null)} />}
      </AnimatePresence>
    </main>
  );
}
